import type { HttpResponse } from './http-client'
import type { Paginated } from '~/shared/interfaces'
import type { ApiDataResponse, ApiResultResponse } from '~/shared/types/api'
import type { ClassConstructor } from '~/shared/types/generics'
import { PaginationDataModel } from '~/shared/model'
import { messages } from '~/shared/const/messages'
import { isUnknownObject } from '~/shared/utils/guards'
import { unwrapDataOrBody, unwrapObject } from '~/shared/utils/response'

export function toModel<T>(modelCtor: ClassConstructor<T>, errorMessage = messages.errors.get) {
  return (res: HttpResponse<unknown>): T => {
    const raw = unwrapDataOrBody(res.data)
    if (isUnknownObject(raw)) {
      return new modelCtor(raw)
    }
    throw new Error(errorMessage)
  }
}

export function toModels<T>(modelCtor: ClassConstructor<T>) {
  return (res: HttpResponse<unknown>): T[] => {
    const raw = unwrapDataOrBody(res.data)
    return Array.isArray(raw) ? raw.map(item => new modelCtor(item)) : []
  }
}

export function toPaginatedModels<T>(modelCtor: ClassConstructor<T>) {
  return (res: HttpResponse<unknown>): Paginated<T> => {
    const list = unwrapObject(res.data)
    const items = list && Array.isArray(list.data) ? list.data : []

    return {
      models: items.map(item => new modelCtor(item)),
      pagination: new PaginationDataModel(list),
    }
  }
}

export function toBooleanResponse(errorMessage = messages.errors.request) {
  return (res: HttpResponse<unknown>): boolean => {
    const raw = unwrapDataOrBody(res.data)
    if (typeof raw === 'boolean') {
      return raw
    }
    throw new Error(errorMessage)
  }
}

export function toBodyModel<T>(modelCtor: ClassConstructor<T>) {
  return (res: HttpResponse<unknown>): T => new modelCtor(res.data ?? {})
}

export function toData<T>() {
  return (res: HttpResponse<ApiDataResponse<T>>): T => res.data.data
}

export function toResult<T>() {
  return (res: HttpResponse<ApiResultResponse<T>>): T => res.data.result
}
